import { supabase, fetchEdgeFunction, fetchPublicEdgeFunction } from "./supabaseClient";

export const mercadoPagoApi = {
  getStatus: () =>
    supabase.functions.invoke("mercadopago-auth/status", { method: "GET" }),

  connect: (credentials: { access_token: string; public_key: string }) =>
    supabase.functions.invoke("mercadopago-auth/connect", {
      method: "POST",
      body: credentials,
    }),

  disconnect: () =>
    supabase.functions.invoke("mercadopago-auth/disconnect", {
      method: "POST",
    }),

  createSession: (data: { items: unknown[]; subtotal: number }) =>
    supabase.functions.invoke("checkout-engine/create-session", {
      method: "POST",
      body: data,
    }),

  createSessionForSale: (data: {
    saleId: string;
    items: unknown[];
    subtotal: number;
    discount?: number;
  }) =>
    supabase.functions.invoke("checkout-engine/create-session-for-sale", {
      method: "POST",
      body: data,
    }),

  getPublicKey: (tenantId: string, sessionId: string, sessionToken: string | null | undefined) =>
    fetchPublicEdgeFunction(
      `checkout-engine/get-gateway-info?tenant_id=${tenantId}&session_id=${sessionId}&session_token=${encodeURIComponent(sessionToken || "")}`
    ),

  getSession: (sessionId: string, sessionToken?: string | null) =>
    fetchPublicEdgeFunction(
      `checkout-engine/session?id=${sessionId}${sessionToken ? `&token=${encodeURIComponent(sessionToken)}` : ""}`
    ),

  processPayment: (sessionId: string, sessionToken: string | null | undefined, paymentData: unknown) =>
    fetchPublicEdgeFunction("checkout-engine/process-payment", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, session_token: sessionToken, payment_data: paymentData }),
    }),
};

export const infinityPayApi = {
  getStatus: () =>
    supabase.functions.invoke("infinitepay-auth/status", { method: "GET" }),

  connect: (data: { handle: string }) =>
    supabase.functions.invoke("infinitepay-auth/connect", {
      method: "POST",
      body: data,
    }),

  disconnect: () =>
    supabase.functions.invoke("infinitepay-auth/disconnect", {
      method: "POST",
    }),

  createLink: (sessionId: string, sessionToken: string | null | undefined) =>
    fetchPublicEdgeFunction("checkout-engine/process-payment", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, session_token: sessionToken }),
    }),
};

export const redeApi = {
  getStatus: () =>
    supabase.functions.invoke("rede-auth/status", { method: "GET" }),

  connect: (credentials: { pv: string; token: string }) =>
    supabase.functions.invoke("rede-auth/connect", {
      method: "POST",
      body: credentials,
    }),

  disconnect: () =>
    supabase.functions.invoke("rede-auth/disconnect", {
      method: "POST",
    }),

  getGatewayInfo: (tenantId: string, sessionId: string, sessionToken: string | null | undefined) =>
    fetchPublicEdgeFunction(
      `checkout-engine/get-gateway-info?tenant_id=${tenantId}&session_id=${sessionId}&session_token=${encodeURIComponent(sessionToken || "")}`
    ),

  processPayment: (sessionId: string, sessionToken: string | null | undefined, paymentData: unknown) =>
    fetchPublicEdgeFunction("checkout-engine/process-payment", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, session_token: sessionToken, payment_data: paymentData }),
    }),

  processPixPayment: (sessionId: string, sessionToken: string | null | undefined) =>
    fetchPublicEdgeFunction("checkout-engine/process-pix", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, session_token: sessionToken }),
    }),
};

export const pagarmeApi = {
  getStatus: () =>
    supabase.functions.invoke("pagarme-auth/status", { method: "GET" }),

  connect: (credentials: { secret_key: string; public_key: string }) =>
    supabase.functions.invoke("pagarme-auth/connect", {
      method: "POST",
      body: credentials,
    }),

  disconnect: () =>
    supabase.functions.invoke("pagarme-auth/disconnect", {
      method: "POST",
    }),

  getGatewayInfo: (tenantId: string, sessionId: string, sessionToken: string | null | undefined) =>
    fetchPublicEdgeFunction(
      `checkout-engine/get-gateway-info?tenant_id=${tenantId}&session_id=${sessionId}&session_token=${encodeURIComponent(sessionToken || "")}`
    ),

  processPayment: (sessionId: string, sessionToken: string | null | undefined, paymentData: unknown) =>
    fetchPublicEdgeFunction("checkout-engine/process-payment", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, session_token: sessionToken, payment_data: paymentData }),
    }),

  processPixPayment: (sessionId: string, sessionToken: string | null | undefined) =>
    fetchPublicEdgeFunction("checkout-engine/process-pix", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, session_token: sessionToken }),
    }),
};

export const cieloApi = {
  getStatus: () =>
    supabase.functions.invoke("cielo-auth/status", { method: "GET" }),

  connect: (credentials: { merchant_id: string; merchant_key: string }) =>
    supabase.functions.invoke("cielo-auth/connect", {
      method: "POST",
      body: credentials,
    }),

  disconnect: () =>
    supabase.functions.invoke("cielo-auth/disconnect", {
      method: "POST",
    }),

  updateSettings: (settings: { max_installments: number }) =>
    supabase.functions.invoke("cielo-auth/update-settings", {
      method: "POST",
      body: settings,
    }),
};

export const nuvemShopApi = {
  getStatus: () => fetchEdgeFunction("nuvem-shop-auth/status"),

  startAuth: () =>
    fetchEdgeFunction("nuvem-shop-auth/start", { method: "POST" }),

  disconnect: () =>
    fetchEdgeFunction("nuvem-shop-auth/disconnect", { method: "POST" }),

  importProducts: (page: number, perPage: number, skipNoStock = false) =>
    fetchEdgeFunction(`nuvem-shop-api/import-products?page=${page}&per_page=${perPage}&skip_no_stock=${skipNoStock}`, {
      method: "POST",
      timeout: 120_000,
    }),
};

export const correiosApi = {
  connect: (credentials: { usuario: string; chave: string; cartao_postagem: string }) =>
    fetchEdgeFunction("correios-auth/connect", {
      method: "POST",
      body: JSON.stringify(credentials),
    }),

  disconnect: () =>
    fetchEdgeFunction("correios-auth/disconnect", { method: "POST" }),

  getStatus: () => fetchEdgeFunction("correios-auth/status"),

  calculateShipping: (params: unknown) =>
    fetchEdgeFunction("correios-api/calculate", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  tracking: (codigo: string) =>
    fetchEdgeFunction("correios-api/tracking", {
      method: "POST",
      body: JSON.stringify({ codigo }),
    }),

  cep: (cep: string) =>
    fetchEdgeFunction("correios-api/cep", {
      method: "POST",
      body: JSON.stringify({ cep }),
    }),

  createShipment: (params: unknown) =>
    fetchEdgeFunction("correios-api/create-shipment", {
      method: "POST",
      body: JSON.stringify(params),
    }),
};

export const mandaBemApi = {
  connect: (credentials: { plataforma_id: string; plataforma_chave: string }) =>
    fetchEdgeFunction("manda-bem-auth/connect", {
      method: "POST",
      body: JSON.stringify(credentials),
    }),

  disconnect: () =>
    fetchEdgeFunction("manda-bem-auth/disconnect", { method: "POST" }),

  getStatus: () => fetchEdgeFunction("manda-bem-auth/status"),

  calculateShipping: (params: unknown) =>
    fetchEdgeFunction("manda-bem-api/calculate", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  createShipment: (params: unknown) =>
    fetchEdgeFunction("manda-bem-api/create-shipment", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  tracking: (envioId: string) =>
    fetchEdgeFunction("manda-bem-api/tracking", {
      method: "POST",
      body: JSON.stringify({ envio_id: envioId }),
    }),
};

export const superFreteApi = {
  connect: (credentials: { api_token: string }) =>
    fetchEdgeFunction("superfrete-auth/connect", {
      method: "POST",
      body: JSON.stringify(credentials),
    }),

  disconnect: () =>
    fetchEdgeFunction("superfrete-auth/disconnect", { method: "POST" }),

  getStatus: () => fetchEdgeFunction("superfrete-auth/status"),

  calculateShipping: (params: unknown) =>
    fetchEdgeFunction("superfrete-api/calculate", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  createShipment: (params: unknown) =>
    fetchEdgeFunction("superfrete-api/create-shipment", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  retryPrint: (shipmentId: string) =>
    fetchEdgeFunction("superfrete-api/retry-print", {
      method: "POST",
      body: JSON.stringify({ shipment_id: shipmentId }),
    }),

  fetchLabel: (shipmentId: string) =>
    fetchEdgeFunction("superfrete-api/fetch-label", {
      method: "POST",
      body: JSON.stringify({ shipment_id: shipmentId }),
    }),

  tracking: (orderId: string) =>
    fetchEdgeFunction("superfrete-api/tracking", {
      method: "POST",
      body: JSON.stringify({ order_id: orderId }),
    }),

  cancel: (shipmentId: string) =>
    fetchEdgeFunction("superfrete-api/cancel", {
      method: "POST",
      body: JSON.stringify({ shipment_id: shipmentId }),
    }),
};

export const melhorEnvioApi = {
  getStatus: () => fetchEdgeFunction("melhor-envio-auth/status"),

  startAuth: (redirectUri: string) =>
    fetchEdgeFunction("melhor-envio-auth/start", {
      method: "POST",
      body: JSON.stringify({ redirect_uri: redirectUri }),
    }),

  disconnect: () =>
    fetchEdgeFunction("melhor-envio-auth/disconnect", { method: "POST" }),

  calculateShipping: (params: unknown) =>
    fetchEdgeFunction("melhor-envio-api/calculate", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  createShipment: (params: unknown) =>
    fetchEdgeFunction("melhor-envio-api/create-shipment", {
      method: "POST",
      body: JSON.stringify(params),
    }),
};

export const blingApi = {
  getStatus: () => fetchEdgeFunction("bling-auth/status"),
  startAuth: () => fetchEdgeFunction("bling-auth/start", { method: "POST" }),
  disconnect: () => fetchEdgeFunction("bling-auth/disconnect", { method: "POST" }),
  importPreview: () => fetchEdgeFunction("bling-api/import-preview", { timeout: 90_000 }),
  importBatch: (page: number) => fetchEdgeFunction("bling-api/import-batch", {
    method: "POST", body: JSON.stringify({ page }), timeout: 120_000,
  }),
  exportPrepare: () => fetchEdgeFunction("bling-api/export-prepare", { timeout: 30_000 }),
  exportBatch: (page: number) => fetchEdgeFunction("bling-api/export-batch", {
    method: "POST", body: JSON.stringify({ page }), timeout: 120_000,
  }),
  syncPrepare: () => fetchEdgeFunction("bling-api/sync-prepare", { timeout: 30_000 }),
  syncBatch: (page: number) => fetchEdgeFunction("bling-api/sync-batch", {
    method: "POST", body: JSON.stringify({ page }), timeout: 60_000,
  }),
};
